import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import MainFooter from '../../components/layout/MainFooter';
import VAHeaderNav from '../../components/navigation/VAHeaderNav';
import Icon from '../../components/AppIcon';
import '../../styles/legal-pages.css';

const TermsOfServicePage = () => {
  const [expandedSection, setExpandedSection] = useState('acceptance');

  const sections = {
    'acceptance': {
      title: 'Acceptance of Terms',
      icon: 'CheckCircle',
      content: [
        {
          heading: 'Agreement',
          text: 'By accessing and using VA Sprouts ("the Platform"), you accept and agree to be bound by all terms and conditions outlined in this Terms of Service agreement. If you do not agree to abide by the above, please do not use this service.'
        },
        {
          heading: 'Eligibility',
          text: 'You must be at least 18 years old to create an account and use the Platform. By creating an account, you represent that you are of legal age and possess the legal authority to enter into this agreement.'
        },
        {
          heading: 'Changes to Terms',
          text: 'VA Sprouts reserves the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting. Continued use of the Platform following notification of changes constitutes your acceptance of the updated Terms.'
        }
      ]
    },
    'user-accounts': {
      title: 'User Accounts',
      icon: 'User',
      content: [
        {
          heading: 'Account Creation',
          text: 'To use the Platform, you must create an account by providing accurate, current, and complete information. You are responsible for maintaining the confidentiality of your password and account credentials. You agree to accept responsibility for all activities that occur under your account.'
        },
        {
          heading: 'Account Information',
          text: 'You must keep your profile information accurate and up-to-date. You agree not to impersonate another person or create duplicate accounts. Your account is personal and non-transferable—you may not sell or transfer your account to another person.'
        },
        {
          heading: 'Account Termination',
          text: 'VA Sprouts may terminate or suspend your account immediately if you violate these Terms or our Community Guidelines. Upon termination, your right to use the Platform ceases immediately. We will not be liable for any loss of data or access to your account.'
        },
        {
          heading: 'Account Security',
          text: 'You are responsible for securing your password and account. Report any unauthorized access immediately. We are not responsible for losses due to compromised credentials.'
        }
      ]
    },
    'user-conduct': {
      title: 'User Conduct & Prohibited Activities',
      icon: 'AlertCircle',
      content: [
        {
          heading: 'Acceptable Use',
          text: 'You agree to use the Platform only for lawful purposes and in ways that do not violate these Terms, applicable laws, or the rights of others. Prohibited conduct includes:\n\n• Violating any applicable laws or regulations\n• Harassing, threatening, or defaming other users\n• Posting discriminatory, racist, sexist, or hateful content\n• Soliciting or engaging in fraud or deception\n• Attempting to bypass security measures\n• Spamming or sending unsolicited communications\n• Uploading malware, viruses, or harmful code\n• Infringing on intellectual property rights'
        },
        {
          heading: 'Content Standards',
          text: 'All content you post must be accurate, professional, and appropriate for a workplace environment. You may not post sexually explicit, violent, or otherwise inappropriate content. You retain ownership of content you create, but grant VA Sprouts a license to use it on the Platform.'
        },
        {
          heading: 'Intellectual Property',
          text: 'By submitting work through the Platform, you represent that you own or have permission to use all intellectual property in that work. You grant employers a license to use the work in accordance with the contract. You are responsible for ensuring no infringement occurs.'
        }
      ]
    },
    'va-responsibilities': {
      title: 'Virtual Assistant Responsibilities',
      icon: 'Briefcase',
      content: [
        {
          heading: 'Skills & Experience',
          text: 'You represent that all information in your profile—including skills, certifications, experience, and hourly rate—is accurate and truthful. Misrepresenting credentials is grounds for account suspension or termination.'
        },
        {
          heading: 'Work Quality',
          text: 'You agree to deliver work that meets the agreed-upon standards, timeline, and specifications. You are responsible for communicating any issues or delays promptly. Late delivery or substandard work may result in dispute resolution and account consequences.'
        },
        {
          heading: 'Confidentiality',
          text: 'You must respect confidentiality agreements and non-disclosure agreements with employers. You may not disclose client information, proprietary data, or work product without explicit permission. Violations may result in legal action and account termination.'
        },
        {
          heading: 'Availability',
          text: 'You are responsible for communicating your availability accurately and maintaining it. If you commit to a project, you agree to honor that commitment. Frequent cancellations or no-shows may result in account restrictions.'
        },
        {
          heading: 'Tax & Legal Compliance',
          text: 'As an independent contractor, you are responsible for all tax obligations, permits, and compliance with local laws. VA Sprouts does not withhold taxes or provide employment benefits. You should consult a tax professional regarding your obligations.'
        }
      ]
    },
    'employer-responsibilities': {
      title: 'Employer Responsibilities',
      icon: 'Store',
      content: [
        {
          heading: 'Job Postings',
          text: 'You represent that job descriptions are accurate, legal, and do not violate any laws. You must not post misleading, fraudulent, or discriminatory job postings. You are responsible for maintaining respectful communication with applicants.'
        },
        {
          heading: 'Fair Treatment',
          text: 'You agree to treat all Virtual Assistants professionally and respectfully. You may not discriminate based on protected characteristics. You must provide clear expectations and timely feedback.'
        },
        {
          heading: 'Payment Obligations',
          text: 'You are responsible for paying Virtual Assistants according to the agreed terms. Late or disputed payments may result in account restrictions or termination. You agree to pay all fees associated with using the Platform.'
        },
        {
          heading: 'Scope Changes',
          text: 'Any changes to project scope, timeline, or deliverables must be agreed upon by both parties. Unilateral scope expansion without compensation adjustment is a violation of these Terms.'
        },
        {
          heading: 'Intellectual Property Rights',
          text: 'Ensure you have the right to authorize work assignments and that any work performed does not infringe on third-party rights. You assume responsibility for any IP disputes related to your projects.'
        }
      ]
    },
    'payments': {
      title: 'Payments & Fees',
      icon: 'CreditCard',
      content: [
        {
          heading: 'Payment Processing',
          text: 'All payments are processed through Stripe. By providing payment information, you authorize VA Sprouts and Stripe to charge your account for fees and services. VA Sprouts is not responsible for payment processing errors by Stripe.'
        },
        {
          heading: 'Refund Policy',
          text: 'Refund requests must be submitted within 30 days of payment. We will review refund requests on a case-by-case basis. Refunds for completed work are at the discretion of the Platform unless required by law.'
        },
        {
          heading: 'Platform Fees',
          text: 'VA Sprouts charges service fees for using the Platform. Fees are disclosed before you pay. We reserve the right to modify fees with 30 days notice. Disputes regarding fees must be reported within 30 days.'
        },
        {
          heading: 'Invoices & Billing',
          text: 'Invoices are issued upon work completion. Payment must be completed within agreed terms. Overdue payments may result in account restrictions and collection action.'
        },
        {
          heading: 'Currency & Taxes',
          text: 'All transactions are in USD unless otherwise specified. Taxes are the responsibility of the parties involved. VA Sprouts does not collect or remit sales tax on behalf of independent contractors.'
        }
      ]
    },
    'disputes': {
      title: 'Dispute Resolution',
      icon: 'Scale',
      content: [
        {
          heading: 'Filing a Dispute',
          text: 'If you have a dispute with another user, submit a formal complaint through our support system within 30 days of the incident. Include documentation, dates, and specific details. We will review both sides fairly.'
        },
        {
          heading: 'Our Role',
          text: 'VA Sprouts serves as a platform facilitating connections. We are not responsible for the performance of contracts between users. However, we may intervene in disputes to enforce our Terms and Community Guidelines.'
        },
        {
          heading: 'Resolution Timeline',
          text: 'We aim to resolve disputes within 7-10 business days. Complex disputes may require additional time. Both parties will be notified of our decision and reasoning.'
        },
        {
          heading: 'Escalation & Appeals',
          text: 'If you disagree with our decision, you may submit an appeal within 14 days with new evidence. A different team member will review your appeal. This is our final resolution step.'
        },
        {
          heading: 'Arbitration',
          text: 'By using the Platform, you agree that disputes will be resolved through binding arbitration rather than court proceedings, except for injunctive relief. Arbitration will be conducted in accordance with JAMS rules.'
        }
      ]
    },
    'limitations': {
      title: 'Limitations of Liability',
      icon: 'Info',
      content: [
        {
          heading: 'As-Is Service',
          text: 'The Platform is provided "as-is" without warranties of any kind. We do not warrant that the Platform will be error-free, uninterrupted, or secure. Your use is at your own risk.'
        },
        {
          heading: 'No Warranty',
          text: 'We disclaim all warranties, express and implied, including merchantability, fitness for a particular purpose, and non-infringement. We do not guarantee that you will find a suitable VA or employer through the Platform.'
        },
        {
          heading: 'Liability Cap',
          text: 'VA Sprouts\' total liability to you for any claim shall not exceed the amount you paid to use the Platform in the past 12 months. This limitation applies regardless of the cause of action.'
        },
        {
          heading: 'Excluded Damages',
          text: 'VA Sprouts is not liable for indirect, incidental, special, consequential, or punitive damages, including lost profits or data loss, even if advised of the possibility of such damages.'
        },
        {
          heading: 'Third-Party Content',
          text: 'VA Sprouts is not responsible for content posted by users, including inaccurate profiles, misleading job postings, or offensive material. We will remove such content upon report.'
        }
      ]
    },
    'intellectual-property': {
      title: 'Intellectual Property Rights',
      icon: 'Copyright',
      content: [
        {
          heading: 'VA Sprouts IP',
          text: 'All Platform content, features, functionality, design, graphics, text, and code ("VA Sprouts IP") are owned by or licensed to VA Sprouts and are protected by copyright and other laws. You may not reproduce, distribute, or transmit any Platform IP without permission.'
        },
        {
          heading: 'User-Generated Content',
          text: 'You retain ownership of content you create. By posting on the Platform, you grant VA Sprouts a non-exclusive, royalty-free license to use, reproduce, and display your content for Platform operations.'
        },
        {
          heading: 'Infringement Claims',
          text: 'If you believe your IP has been infringed, contact us immediately with evidence. We will investigate and take appropriate action, including content removal if warranted.'
        }
      ]
    },
    'privacy': {
      title: 'Privacy & Data Protection',
      icon: 'Lock',
      content: [
        {
          heading: 'Privacy Policy',
          text: 'Your use of the Platform is governed by our Privacy Policy, which is incorporated into these Terms. Please review it carefully to understand our data practices.'
        },
        {
          heading: 'Data Security',
          text: 'We implement security measures to protect your data. However, no system is completely secure. You are responsible for protecting your own passwords and confidential information.'
        },
        {
          heading: 'Data Retention',
          text: 'We retain data as necessary to provide services and comply with legal obligations. You can request deletion of your data, though some information may be retained for legal or business purposes.'
        }
      ]
    },
    'termination': {
      title: 'Termination & Suspension',
      icon: 'X',
      content: [
        {
          heading: 'Termination by User',
          text: 'You may terminate your account at any time through your account settings. Upon termination, you lose access to the Platform and your profile is no longer visible.'
        },
        {
          heading: 'Termination by VA Sprouts',
          text: 'We may terminate your account immediately if you violate these Terms, engage in prohibited conduct, or threaten the Platform\'s integrity. We reserve the right to terminate accounts at our discretion.'
        },
        {
          heading: 'Suspension',
          text: 'We may temporarily suspend your account for investigation, maintenance, or policy violations. Suspension is typically temporary; termination is permanent.'
        },
        {
          heading: 'Survival',
          text: 'Provisions regarding indemnification, limitation of liability, IP rights, and arbitration survive termination of your account and these Terms.'
        }
      ]
    },
    'indemnification': {
      title: 'Indemnification',
      icon: 'Shield',
      content: [
        {
          heading: 'Your Indemnification Obligation',
          text: 'You agree to defend, indemnify, and hold harmless VA Sprouts, its officers, directors, employees, and agents from any claims, damages, or expenses (including legal fees) arising from your use of the Platform, your violation of these Terms, or your infringement of third-party rights.'
        },
        {
          heading: 'Exception',
          text: 'This indemnification does not apply to claims arising from VA Sprouts\' negligence or willful misconduct.'
        }
      ]
    },
    'compliance': {
      title: 'Compliance & Legal',
      icon: 'Gavel',
      content: [
        {
          heading: 'Governing Law',
          text: 'These Terms are governed by the laws of the United States, specifically the laws applicable to business transactions, without regard to conflict of law principles.'
        },
        {
          heading: 'Legal Compliance',
          text: 'You agree to comply with all applicable laws, regulations, and third-party rights. VA Sprouts complies with GDPR, CCPA, and other applicable data protection laws.'
        },
        {
          heading: 'Export Compliance',
          text: 'The Platform may not be available in all jurisdictions. You are responsible for ensuring your use complies with applicable export control laws.'
        },
        {
          heading: 'Severability',
          text: 'If any provision of these Terms is found to be unenforceable, that provision will be modified to the minimum extent necessary, or severed, and the remaining provisions remain in effect.'
        }
      ]
    },
    'contact': {
      title: 'Contact & Support',
      icon: 'Mail',
      content: [
        {
          heading: 'Questions or Concerns',
          text: 'If you have questions about these Terms of Service, contact us at:\n\nEmail: legal@vasprouts.com\nSupport Portal: /help-support\nMailing Address: VA Sprouts, Inc.\n\nWe will respond to inquiries within 14 business days.'
        },
        {
          heading: 'Notices',
          text: 'We may provide notices through email or by posting on the Platform. Notices are effective upon delivery. You can opt out of certain notices through your account settings.'
        }
      ]
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Terms of Service | VA Sprouts</title>
        <meta name="description" content="Read the Terms of Service for VA Sprouts. Understand your rights, responsibilities, and our policies." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <VAHeaderNav />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
            <p className="text-xl text-purple-100 max-w-2xl mx-auto">
              Please read and understand our Terms of Service before using the VA Sprouts platform.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Table of Contents */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Navigation</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(sections).map(([key, section]) => (
              <button
                key={key}
                onClick={() => toggleSection(key)}
                className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-purple-600 hover:shadow-md transition-all text-left"
              >
                <Icon name={section.icon} size={24} className="text-purple-600 flex-shrink-0" />
                <span className="font-medium text-gray-900 hover:text-purple-600 transition-colors text-sm">{section.title}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Terms Sections */}
        <div className="space-y-6">
          {Object.entries(sections).map(([key, section]) => (
            <div key={key} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleSection(key)}
                className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Icon name={section.icon} size={24} className="text-purple-600" />
                  <h2 className="text-xl font-bold text-gray-900">{section.title}</h2>
                </div>
                <Icon
                  name={expandedSection === key ? 'ChevronUp' : 'ChevronDown'}
                  size={24}
                  className="text-gray-500 transition-transform"
                />
              </button>

              {expandedSection === key && (
                <div className="border-t border-gray-200 px-6 py-6 space-y-6">
                  {section.content.map((item, idx) => (
                    <div key={idx}>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.heading}</h3>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">{item.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Important Notice */}
        <div className="mt-12 bg-red-50 rounded-lg border border-red-200 p-8">
          <div className="flex gap-4">
            <Icon name="AlertCircle" size={32} className="text-red-600 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Important Acknowledgments</h2>
              <ul className="space-y-2 text-gray-700">
                <li>✓ By using the Platform, you accept all terms and conditions</li>
                <li>✓ VA Sprouts is not liable for user disputes or non-performance</li>
                <li>✓ You are responsible for understanding tax implications of your transactions</li>
                <li>✓ Violations may result in account suspension or termination</li>
                <li>✓ All disputes are resolved through binding arbitration, not courts</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Last Updated */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p><strong>Last updated:</strong> January 2026</p>
          <p>These Terms are effective immediately upon posting. We may update them at any time.</p>
          <p className="mt-2">For questions: <Link to="/help-support" className="text-purple-600 hover:underline">Contact Support</Link></p>
        </div>
      </div>

      <MainFooter />
    </div>
  );
};

export default TermsOfServicePage;
